import React, { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import api, { formatApiErrorDetail } from "../../lib/api";
import { DriverIssueReport } from "../../components/driver/DriverIssueReport";

export default function DriverIssuePage() {
  const { user, ready } = useAuth();
  const nav = useNavigate();
  const [search] = useSearchParams();
  const [job, setJob] = useState(null);
  const [fallbackJob, setFallbackJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const jobId = search.get("jobId");

  useEffect(() => {
    if (!jobId) {
      api.get("/driver/me/jobs").then(({ data }) => setFallbackJob(data.current_job || data.next_job || null)).catch(() => setFallbackJob(null));
      return;
    }
    api.get(`/driver/jobs/${jobId}`).then(({ data }) => setJob(data.job)).catch(() => setJob(null));
  }, [jobId]);

  if (ready && !user) return <Navigate to="/driver/login" replace />;
  if (ready && user?.role !== "driver") return <Navigate to="/admin" replace />;
  if (ready && user?.requires_password_change) return <Navigate to="/driver/set-password" replace />;

  const activeJob = job || fallbackJob;

  const submit = async ({ issue_type, severity, description, files, vehicle_id }) => {
    setBusy(true);
    try {
      const uploaded = [];
      for (const file of files || []) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/driver/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.path);
      }
      await api.post("/driver/issues", { driver_job_id: jobId || activeJob?.id || null, vehicle_id: vehicle_id || activeJob?.assigned_vehicle_id || null, issue_type, severity, description, photos: uploaded });
      setSuccess(true);
      toast.success("Vehicle issue reported to admin.");
      setTimeout(() => nav(jobId ? `/driver/job/${jobId}` : "/driver"), 800);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-5 sm:px-6" data-testid="driver-issue-page">
      <div className="mx-auto max-w-md space-y-4">
        <Link to={jobId ? `/driver/job/${jobId}` : "/driver"} className="inline-flex items-center gap-2 text-sm font-medium text-slate-600"><ArrowLeft size={14} /> Back</Link>
        {!activeJob && <p className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600">No active vehicle context yet. Open a job first, or wait for your next assigned task.</p>}
        <DriverIssueReport initialVehicleId={activeJob?.assigned_vehicle_id || ""} onSubmit={submit} busy={busy || !activeJob} successMessage={success} />
      </div>
    </div>
  );
}